import React, { FC, useMemo, useState } from 'react';
import {
  AlignEnum,
  ColorNameEnum,
  InputStyle,
  InputStyleWrapper,
  Select,
  SizeEnum,
  Text,
  WeightEnum,
} from '@getvim/atomic-ui';
import { Provider } from '@getvim/internal-vim-os-sdk/types';
import { startCase, debounce } from 'lodash-es';
import LRU from 'lru-cache';
import { Tab, WidgetOutgoingEventName } from '../../types';
import {
  NO_RESULTS,
  START_SEARCH_TWO_CHARS,
  SUBMIT_PROVIDER_ERROR,
} from '../../consts/selectInputMessages';
import './searchProviders.less';
import { searchMockedProviders } from '../../mocks/mock/searchMockedProviders';
import { createLogger, getParamFromUrlQueryString } from '../../utils';
import { useApi } from '@getvim/internal-vim-os-sdk/react';

const priorAuthLogger = createLogger('SearchProviders');

const widgetId = 'prior-auth-v2';

type ProviderSelectOption = {
  label: string;
  value: Provider;
};

const formatOptions = (providers: Provider[]): ProviderSelectOption[] => {
  return providers.map((provider) => {
    const providerNames = [provider.demographics.lastName, provider.demographics.firstName]
      .map((n) => {
        if (!n) {
          return '';
        }
        return startCase(n.toLowerCase());
      })
      .filter(Boolean);

    const label = providerNames?.length > 1 ? providerNames.join(', ') : providerNames[0];
    return {
      value: provider,
      label,
    };
  });
};

interface SearchProvidersProps {
  selectedProvider: Provider | undefined;
  setSelectedProvider: (providers: Provider | undefined) => void;
  currentTab: Tab;
  isDisabled: boolean;
}

const cacheTTL = 15 * 1000 * 60; // 15 minutes
const searchResultsCache = new LRU({ max: 500, ttl: cacheTTL });

const tabContent = {
  [Tab.SubmitCase]: {
    title: 'Submitting provider', // todo: replace with "Search provider"
    description: 'Select the submitting provider for this prior authorization',
  },
};

const SearchProviders: FC<SearchProvidersProps> = ({
  setSelectedProvider,
  selectedProvider,
  currentTab,
  isDisabled = false,
}: SearchProvidersProps) => {
  const { ehrActions } = useApi();
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Provider[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSearch = async (searchTerm: string) => {
    try {
      const cachedResults = searchResultsCache.get(searchTerm);
      if (cachedResults) {
        const results = cachedResults as Provider[];
        priorAuthLogger.info(`searching "${searchTerm}" - fetching results from local cache`, {
          results,
          noPHI: true,
        });
        setSearchResults(results);
      }
      // uncomment the following line to use mocked results:
      // const results = searchMockedProviders(searchTerm);
      const { results } = (await ehrActions.searchProviders({ searchTerm: searchTerm })) as any;
      searchResultsCache.set(searchTerm, results);
      setSearchResults(results);
    } catch (error: any) {
      priorAuthLogger.error(
        `error when searching "${searchTerm}" - cannot send search query to the hub`,
        { error, noPHI: true },
      );
      setSearchResults([]);
    }
    setIsLoading(false);
  };

  const onProvidersChange = (items: ProviderSelectOption[]) => {
    if (items?.length) {
      const selectedItem = items[0].value;
      // add this condition, otherwise the provider can be set to undefined its being selected
      if (selectedItem) {
        setSelectedProvider(selectedItem);
        setSearchResults(undefined);
      }
    } else {
      setSelectedProvider(undefined);
      setIsTouched(true);
    }
    if (!selectedProvider) {
      setSearchResults(undefined);
    }
  };

  const selectedValue = useMemo(() => {
    if (selectedProvider) return formatOptions([selectedProvider]);

    return [];
  }, [selectedProvider]);

  const onSearchInputChanged = (args) => {
    const searchTerm = args.state.search;

    if (searchTerm?.length < 2) {
      setSearchResults(undefined);
      return undefined;
    }

    setIsLoading(true);
    debounce((bounchSearchTerm) => {
      void onSearch(bounchSearchTerm);
    }, 500)(searchTerm);
    return formatOptions(searchResults ?? []);
  };

  return (
    <div className="search-providers margin-bottom-20">
      <div>
        <Text
          text="Search provider"
          colorName={ColorNameEnum.ultraDark}
          weight={WeightEnum.bold}
          size={SizeEnum['14px']}
          className="search-providers__title"
        />
        <Text
          text={tabContent[currentTab]?.description}
          colorName={ColorNameEnum.ultraDark}
          weight={WeightEnum.normal}
          size={SizeEnum['12px']}
          className="search-providers__description"
        />
      </div>

      <div>
        <InputStyleWrapper
          leftIcon={<i className="icon-search" />}
          inputStyle={InputStyle.medium}
          className="search-provider-input-wrapper input-wrapper "
        >
          <div className="input">
            <Select
              label="Provider"
              id={`providers-select-${currentTab}`}
              name="providers"
              options={formatOptions(searchResults ?? [])}
              values={selectedValue}
              onChange={(items) => {
                onProvidersChange(items);
              }}
              searchFn={onSearchInputChanged}
              noDataLabel={searchResults ? NO_RESULTS : START_SEARCH_TWO_CHARS}
              clearable={!!selectedProvider}
              isDisabled={isDisabled}
              loading={isLoading}
              hideValueOnInput
              noResultsLabel={NO_RESULTS}
              noInputLabel={START_SEARCH_TWO_CHARS}
              showChevronIcon={!selectedProvider}
            />
          </div>
        </InputStyleWrapper>
      </div>
      {isTouched && !selectedProvider && (
        <div className="margin-top-5 error-message">
          <Text
            text={SUBMIT_PROVIDER_ERROR}
            weight={WeightEnum.medium}
            size={SizeEnum['12px']}
            align={AlignEnum.right}
          />
        </div>
      )}
    </div>
  );
};

export default SearchProviders;
